import { useCallback, useEffect, useRef, useState } from "react";
import baseUrl from "../../api/baseUrl";
import type { CatalogCategory, PriceListRow } from "./labCatalog";

let catalogCache: { at: number; data: CatalogCategory[] } | null = null;
const CACHE_MS = 5 * 60 * 1000;

/** Care32's master lab catalogue (active items), shared across screens for a few minutes. */
export function useLabCatalog(enabled = true) {
  const [catalog, setCatalog] = useState<CatalogCategory[]>(catalogCache?.data ?? []);
  const [loading, setLoading] = useState(enabled && !catalogCache);

  useEffect(() => {
    if (!enabled) return;
    if (catalogCache && Date.now() - catalogCache.at < CACHE_MS) {
      setCatalog(catalogCache.data);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    baseUrl
      .get("client/labs/catalog")
      .then((res) => {
        const data: CatalogCategory[] = res.data.data ?? [];
        catalogCache = { at: Date.now(), data };
        if (!cancelled) setCatalog(data);
      })
      .catch(() => {
        if (!cancelled) setCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { catalog, loading };
}

/** Price list query for a lab: registered labs by labId, hand-added by partner. */
export function priceListParams(labId: string, labPartnerId: string) {
  if (labId) return { labId };
  if (labPartnerId) return { labPartnerId };
  return null;
}

/**
 * The chosen lab's price list for the order form. Switching labs mid-request
 * never lets the old lab's list land.
 */
export function useLabPriceList(labId: string, labPartnerId: string, enabled = true) {
  const [items, setItems] = useState<PriceListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const request = useRef(0);

  const load = useCallback(() => {
    const params = priceListParams(labId, labPartnerId);
    const id = ++request.current;
    if (!params || !enabled) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    baseUrl
      .get("client/labs/items", { params })
      .then((res) => {
        if (id === request.current) setItems(res.data.data ?? []);
      })
      .catch(() => {
        if (id === request.current) setItems([]);
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [labId, labPartnerId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { items, loading, reload: load };
}
